import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FieldLabel,
  FormGrid,
  FormPanel,
  Icon,
  IconButton,
  ListRow,
  PageHeader,
  RowChip,
  SelectField,
  TableCard,
  Tag,
  TextField,
  Toggle,
  type Column,
} from '@sparkery/ui';
import {
  acceptAttribute,
  type AssetFile,
  type AssetFolder,
  type FolderGrantee,
} from '@sparkery/contracts';
import { fileSize, shortDate, useAssets } from '../../lib/workspace.ts';
import {
  useAssetDownload,
  useAssetUpload,
  useCreateFolder,
  useDeleteAsset,
  useDeleteFolder,
  useGrantFolder,
  useRevokeFolder,
} from '../../lib/assets.ts';
import { plural } from '../../lib/report.tsx';
import { stringParam } from '../route-params.ts';
import './assets.css';

/**
 * The brand library, plus whatever is private to this partner.
 *
 * The root shows folders; choosing one moves to the folder route. This matches the
 * prototype's two states while making the URL shareable instead of keeping the selected
 * folder as local page state.
 *
 * **Every management control here is offered only when the server said it may be.**
 * The storage bucket still controls whether uploads/downloads can complete, but the
 * UI remains visible in local environments so the admin asset flows can be aligned.
 */
export function AssetsView() {
  const assets = useAssets();
  const navigate = useNavigate({ from: '/assets' });
  const deleteFolder = useDeleteFolder();
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<AssetFolder | null>(null);
  const data = assets.data;

  const canManage = data?.canManage ?? false;
  const columns: Column<AssetFolder>[] = [
    {
      id: 'type',
      header: 'Type',
      width: 64,
      cell: (row) => (
        <RowChip tint={folderTint(data?.folders.indexOf(row) ?? 0, row)} size={28}>
          <Icon name="folder" size={14} />
        </RowChip>
      ),
    },
    {
      id: 'name',
      header: 'Name',
      strong: true,
      cell: (row) => row.name,
    },
    {
      id: 'contents',
      header: 'Contents',
      numeric: true,
      width: 120,
      cell: (row) => `${row.fileCount} ${row.fileCount === 1 ? 'file' : 'files'}`,
    },
    ...(canManage
      ? [
          {
            id: 'visibility',
            header: 'Visibility',
            width: 130,
            cell: (row: AssetFolder) =>
              row.isPublic ? (
                <Tag tone="public">Public</Tag>
              ) : (
                <Tag tone="neutral">{plural(row.grantedTo.length, 'partner')}</Tag>
              ),
          },
        ]
      : []),
  ];

  function openFolder(folder: AssetFolder) {
    void navigate({ to: '/assets/$folderId', params: { folderId: folder.id } });
  }

  function folderActions(folder: AssetFolder) {
    return (
      <IconButton
        label={`Delete ${folder.name}`}
        icon={<Icon name="trash" size={16} />}
        danger
        disabled={deleteFolder.isPending}
        onClick={(event) => {
          event.stopPropagation();
          setDeleteFolderTarget(folder);
        }}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Assets"
        subtitle={
          data?.showsAllPartners
            ? 'Brand library and every partner folder'
            : 'Logos, product imagery and copy'
        }
        actions={
          canManage ? (
            <Button
              variant={showNewFolder ? 'quiet' : 'ink'}
              {...(showNewFolder ? {} : { icon: 'plus' })}
              onClick={() => setShowNewFolder((current) => !current)}
            >
              {showNewFolder ? 'Hide form' : 'New folder'}
            </Button>
          ) : null
        }
      />

      {assets.isError ? (
        <ErrorState
          title="Could not load assets"
          {...(assets.error instanceof Error ? { detail: assets.error.message } : {})}
          onRetry={() => void assets.refetch()}
        />
      ) : null}

      {canManage && showNewFolder ? (
        <NewFolderForm onCancel={() => setShowNewFolder(false)} />
      ) : null}

      {deleteFolder.error ? (
        <p className="assetsError" role="alert">
          {deleteFolder.error instanceof Error
            ? deleteFolder.error.message
            : 'Could not delete that folder.'}
        </p>
      ) : null}
      {deleteFolder.data && deleteFolder.data.objectsOrphaned > 0 ? (
        <p className="assetsError" role="alert">
          {deleteFolder.data.objectsOrphaned} file
          {deleteFolder.data.objectsOrphaned === 1 ? '' : 's'} could not be removed from storage.
          The records are gone; the leftover objects are named in the audit log.
        </p>
      ) : null}

      <ConfirmDialog
        open={deleteFolderTarget !== null}
        title="Delete folder?"
        body={
          deleteFolderTarget
            ? `Delete ${deleteFolderTarget.name} and its ${plural(
                deleteFolderTarget.fileCount,
                'file',
              )}? This cannot be undone.`
            : undefined
        }
        confirmLabel={deleteFolder.isPending ? 'Deleting…' : 'Delete'}
        confirmDisabled={deleteFolder.isPending}
        danger
        onCancel={() => setDeleteFolderTarget(null)}
        onConfirm={() => {
          if (!deleteFolderTarget) return;
          deleteFolder.mutate(deleteFolderTarget.id, {
            onSuccess: () => setDeleteFolderTarget(null),
          });
        }}
      />

      <TableCard
        title="Brand assets"
        meta={data ? plural(data.folders.length, 'folder') : undefined}
        className="assetsRootCard"
      >
        <DataTable
          caption="Asset folders"
          columns={columns}
          rows={data?.folders ?? []}
          rowKey={(row) => row.id}
          loading={assets.isPending}
          onRowClick={openFolder}
          {...(canManage ? { rowAction: folderActions } : {})}
          empty={
            <EmptyState
              title="No folders yet"
              body={
                canManage
                  ? 'Create a folder to start uploading brand material.'
                  : 'Sparkery will share logos, imagery and copy here.'
              }
            />
          }
          mobileCard={(row) => (
            <ListRow
              lead={
                <RowChip tint={folderTint(data?.folders.indexOf(row) ?? 0, row)} size={40}>
                  <Icon name="folder" size={18} />
                </RowChip>
              }
              title={row.name}
              meta={`${row.fileCount} ${row.fileCount === 1 ? 'file' : 'files'} · ${
                row.isPublic
                  ? 'Public'
                  : canManage
                    ? plural(row.grantedTo.length, 'partner')
                    : 'Private'
              }`}
              trailing={row.isPublic ? null : <Tag tone="neutral">Private</Tag>}
              actions={
                canManage ? (
                  folderActions(row)
                ) : (
                  <Icon name="chevron-right" size={18} color="var(--on-surface-muted)" />
                )
              }
              onClick={() => openFolder(row)}
            />
          )}
        />
      </TableCard>
    </>
  );
}

export function AssetFolderView() {
  const params: unknown = useParams({ from: '/assets/$folderId' });
  const folderId = stringParam(params, 'folderId');
  const navigate = useNavigate({ from: '/assets/$folderId' });
  const [showUpload, setShowUpload] = useState(false);
  const assets = useAssets();
  const data = assets.data;
  const folder = data?.folders.find((row) => row.id === folderId);

  const canManage = data?.canManage ?? false;
  const storageConfigured = data?.storageConfigured ?? false;

  return (
    <>
      <PageHeader
        title="Assets"
        subtitle={
          data?.showsAllPartners
            ? 'Brand library and every partner folder'
            : 'Logos, product imagery and copy'
        }
        actions={
          folder && canManage ? (
            <Button
              variant={showUpload ? 'quiet' : 'ink'}
              {...(showUpload ? {} : { icon: 'upload' })}
              onClick={() => setShowUpload((current) => !current)}
            >
              {showUpload ? 'Hide form' : 'Upload file'}
            </Button>
          ) : null
        }
      />

      {assets.isError ? (
        <ErrorState
          title="Could not load assets"
          {...(assets.error instanceof Error ? { detail: assets.error.message } : {})}
          onRetry={() => void assets.refetch()}
        />
      ) : null}

      {!assets.isPending && !folder ? (
        <ErrorState
          title="Folder not found"
          detail="It may have been deleted, or your account may no longer have access."
          onRetry={() => void navigate({ to: '/assets' })}
        />
      ) : null}

      {folder && canManage && showUpload ? (
        <UploadPanel
          folder={folder}
          maxUploadBytes={data?.maxUploadBytes ?? 1}
          onCancel={() => setShowUpload(false)}
        />
      ) : null}

      {folder ? (
        <FolderPanel
          folder={folder}
          canManage={canManage}
          storageConfigured={storageConfigured}
          partners={data?.grantablePartners ?? []}
          onBack={() => void navigate({ to: '/assets' })}
        />
      ) : null}
    </>
  );
}

function folderTint(index: number, folder: AssetFolder): 'cyan' | 'pink' | 'amber' | 'green' {
  if (!folder.isPublic) return 'amber';
  return (['cyan', 'pink', 'amber', 'cyan'] as const)[index % 4] ?? 'cyan';
}

function fileTint(file: AssetFile): 'cyan' | 'pink' | 'amber' {
  if (file.kind.match(/^(png|jpg|jpeg|webp|gif|svg)$/i)) return 'cyan';
  if (file.kind.match(/^(ppt|pptx|key|pdf)$/i)) return 'amber';
  return 'pink';
}

function fileIcon(file: AssetFile): 'image' | 'deck' | 'doc' {
  if (file.kind.match(/^(png|jpg|jpeg|webp|gif|svg)$/i)) return 'image';
  if (file.kind.match(/^(ppt|pptx|key|pdf)$/i)) return 'deck';
  return 'doc';
}

/**
 * One folder's files, and — for staff — its controls.
 *
 * The upload target is the folder, not the index. Keeping this on the routed folder
 * page gives the same two-state shape as the prototype and leaves a URL support can
 * share directly.
 */
function FolderPanel({
  folder,
  canManage,
  storageConfigured,
  partners,
  onBack,
}: {
  folder: AssetFolder;
  canManage: boolean;
  storageConfigured: boolean;
  partners: FolderGrantee[];
  onBack: () => void;
}) {
  const download = useAssetDownload();
  const deleteAsset = useDeleteAsset();
  const [deleteFileTarget, setDeleteFileTarget] = useState<AssetFile | null>(null);

  function fileActions(file: AssetFile) {
    return (
      <span className="assetsRowActions">
        <IconButton
          label={download.pending === file.id ? `Opening ${file.name}` : `Download ${file.name}`}
          icon={<Icon name={download.pending === file.id ? 'check' : 'download'} size={16} />}
          disabled={!storageConfigured || download.pending === file.id}
          onClick={() => void download.download(file.id)}
        />
        {canManage ? (
          <IconButton
            label={`Delete ${file.name}`}
            icon={<Icon name="trash" size={16} />}
            danger
            disabled={deleteAsset.isPending}
            onClick={() => setDeleteFileTarget(file)}
          />
        ) : null}
      </span>
    );
  }

  const fileColumns: Column<AssetFile>[] = [
    {
      id: 'type',
      header: 'Type',
      width: 64,
      cell: (file) => (
        <RowChip tint={fileTint(file)} size={28}>
          <Icon name={fileIcon(file)} size={14} />
        </RowChip>
      ),
    },
    {
      id: 'name',
      header: 'Name',
      strong: true,
      cell: (file) => (
        <span className="assetsFileName">
          <span className="assetsKind">{file.kind}</span>
          {file.name}
        </span>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      numeric: true,
      width: 110,
      cell: (file) => fileSize(file.sizeBytes),
    },
    {
      id: 'updated',
      header: 'Updated',
      numeric: true,
      secondary: true,
      cell: (file) => shortDate(file.updatedAt),
    },
  ];

  return (
    <Card pad="0">
      <div className="assetsFolderHead">
        <div className="assetsFolderHeading">
          <IconButton
            label="Back to asset folders"
            icon={<Icon name="level-up" size={16} />}
            onClick={onBack}
          />
          <p className="typeCardTitle">{folder.name}</p>
        </div>
        <span className="cardHeaderMeta assetsFolderMeta">{plural(folder.fileCount, 'file')}</span>
      </div>

      {download.error ? (
        <p className="assetsError" role="alert">
          {download.error}
        </p>
      ) : null}
      {deleteAsset.error ? (
        <p className="assetsError" role="alert">
          {deleteAsset.error instanceof Error
            ? deleteAsset.error.message
            : 'Could not delete that file.'}
        </p>
      ) : null}
      {canManage && !folder.isPublic ? <GrantList folder={folder} partners={partners} /> : null}

      <ConfirmDialog
        open={deleteFileTarget !== null}
        title="Delete file?"
        body={
          deleteFileTarget
            ? `Delete ${deleteFileTarget.name} from ${folder.name}? This cannot be undone.`
            : undefined
        }
        confirmLabel={deleteAsset.isPending ? 'Deleting…' : 'Delete'}
        confirmDisabled={deleteAsset.isPending}
        danger
        onCancel={() => setDeleteFileTarget(null)}
        onConfirm={() => {
          if (!deleteFileTarget) return;
          deleteAsset.mutate(deleteFileTarget.id, {
            onSuccess: () => setDeleteFileTarget(null),
          });
        }}
      />

      <DataTable
        caption={`Files in ${folder.name}`}
        columns={fileColumns}
        rows={folder.files}
        rowKey={(file) => file.id}
        rowAction={fileActions}
        mobileCard={(file) => (
          <ListRow
            lead={
              <RowChip tint={fileTint(file)} size={40}>
                <Icon name={fileIcon(file)} size={18} />
              </RowChip>
            }
            title={file.name}
            meta={`${file.kind.toUpperCase()} · ${fileSize(file.sizeBytes)} · ${shortDate(
              file.updatedAt,
            )}`}
            actions={fileActions(file)}
          />
        )}
        empty={
          <EmptyState
            title="This folder is empty"
            body={
              canManage
                ? 'Use the upload action above to add files.'
                : 'Nothing has been uploaded here yet.'
            }
          />
        }
      />
    </Card>
  );
}

/**
 * Who a private folder reaches.
 *
 * Shown only for a private folder, because a grant on a public one means nothing — the
 * server refuses it, and offering the control would be inviting a 422.
 */
function GrantList({ folder, partners }: { folder: AssetFolder; partners: FolderGrantee[] }) {
  const grant = useGrantFolder();
  const revoke = useRevokeFolder();
  const [choice, setChoice] = useState('');

  const grantedIds = new Set(folder.grantedTo.map((grantee) => grantee.partnerId));
  const available = partners.filter((partner) => !grantedIds.has(partner.partnerId));
  const failure = grant.error ?? revoke.error;

  return (
    <div className="assetsGrants">
      <p className="assetsGrantsLabel">Visible to</p>

      {folder.grantedTo.length === 0 ? (
        <p className="assetsNote">Nobody yet.</p>
      ) : (
        <ul className="assetsGrantChips">
          {folder.grantedTo.map((grantee) => (
            <li key={grantee.partnerId} className="assetsGrantChip">
              {grantee.name}
              <IconButton
                label={`Remove ${grantee.name}`}
                disabled={revoke.isPending}
                onClick={() => revoke.mutate({ folderId: folder.id, partnerId: grantee.partnerId })}
                icon={<Icon name="close" size={12} />}
              />
            </li>
          ))}
        </ul>
      )}

      {available.length > 0 ? (
        <div className="assetsGrantAdd">
          <SelectField
            id={`grant-${folder.id}`}
            label={<span className="visuallyHidden">Grant {folder.name} to a partner</span>}
            selectClassName="controlInline"
            value={choice}
            onChange={(event) => setChoice(event.target.value)}
            options={[
              { value: '', label: 'Choose a partner…' },
              ...available.map((partner) => ({ value: partner.partnerId, label: partner.name })),
            ]}
          />
          <Button
            size="small"
            variant="secondary"
            disabled={!choice || grant.isPending}
            onClick={() =>
              grant.mutate(
                { folderId: folder.id, partnerId: choice },
                { onSuccess: () => setChoice('') },
              )
            }
          >
            Grant access
          </Button>
        </div>
      ) : null}

      {failure ? (
        <p className="assetsError" role="alert">
          {failure instanceof Error ? failure.message : 'Could not change access.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * File upload form.
 *
 * The design-system admin assets screen uses an inline FormPanel for uploads. The
 * name field is wired through by creating a renamed File before handing off to the
 * storage upload hook.
 */
function UploadPanel({
  folder,
  maxUploadBytes,
  onCancel,
}: {
  folder: AssetFolder;
  maxUploadBytes: number;
  onCancel: () => void;
}) {
  const { upload, busy, progress, clear } = useAssetUpload(folder.id);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const tooLarge = file ? file.size > maxUploadBytes : false;

  function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const next = event.target.files?.[0] ?? null;
    setFile(next);
    if (next) setName(nameWithoutExtension(next.name));
  }

  function submit() {
    if (!file || tooLarge) return;

    const filename = uploadFilename(file, name);
    const uploadFile =
      filename === file.name
        ? file
        : new File([file], filename, { type: file.type, lastModified: file.lastModified });

    void upload([uploadFile]);
  }

  return (
    <div className="assetsUpload">
      <FormPanel
        title={`Upload file to ${folder.name}`}
        confirmLabel={busy ? 'Uploading…' : 'Upload'}
        confirmDisabled={!file || tooLarge || busy}
        onCancel={onCancel}
        onConfirm={submit}
        note={
          tooLarge ? `Files can be up to ${Math.round(maxUploadBytes / 1024 / 1024)}MB.` : undefined
        }
      >
        <FormGrid>
          <TextField
            id={`asset-file-name-${folder.id}`}
            label="File name"
            value={name}
            placeholder="Autumn banner set"
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
          />
          <div className="assetsFileField">
            <FieldLabel htmlFor={`asset-file-${folder.id}`}>File</FieldLabel>
            <input
              id={`asset-file-${folder.id}`}
              type="file"
              className="assetsFileInput"
              accept={acceptAttribute()}
              disabled={busy}
              onChange={selectFile}
            />
          </div>
        </FormGrid>
      </FormPanel>

      {progress.length > 0 ? (
        <ul className="assetsProgress">
          {progress.map((row) => (
            <li key={row.filename} className="assetsProgressRow" data-phase={row.phase}>
              <span className="assetsProgressName">{row.filename}</span>
              <span className="assetsProgressState">{phaseLabel(row)}</span>
              <span
                className="assetsProgressBar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                {...(row.fraction === null
                  ? {}
                  : { 'aria-valuenow': Math.round(row.fraction * 100) })}
              >
                <span
                  className="assetsProgressFill"
                  style={{ inlineSize: `${Math.round((row.fraction ?? 0) * 100)}%` }}
                />
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {progress.length > 0 && !busy ? (
        <Button variant="ghost" size="small" onClick={clear}>
          Clear
        </Button>
      ) : null}
    </div>
  );
}

function nameWithoutExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(0, dot) : filename;
}

function uploadFilename(file: File, name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return file.name;
  if (trimmed.includes('.')) return trimmed;

  const dot = file.name.lastIndexOf('.');
  const extension = dot > 0 ? file.name.slice(dot) : '';
  return `${trimmed}${extension}`;
}

/** The phase, in words. Failures show the reason, which is usually actionable. */
function phaseLabel(row: { phase: string; fraction: number | null; error?: string }): string {
  switch (row.phase) {
    case 'signing':
      return 'Preparing…';
    case 'uploading':
      return row.fraction === null ? 'Uploading…' : `${Math.round(row.fraction * 100)}%`;
    case 'recording':
      return 'Saving…';
    case 'done':
      return 'Uploaded';
    default:
      return row.error ?? 'Failed';
  }
}

/** Creating a folder. Private by default — a forgotten private folder leaks nothing. */
function NewFolderForm({ onCancel }: { onCancel: () => void }) {
  const create = useCreateFolder();
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  function submit() {
    if (name.trim()) {
      create.mutate(
        { name: name.trim(), isPublic },
        {
          onSuccess: () => {
            setName('');
            onCancel();
          },
        },
      );
    }
  }

  return (
    <FormPanel
      title="New folder"
      confirmLabel={create.isPending ? 'Creating…' : 'Create folder'}
      confirmDisabled={!name.trim() || create.isPending}
      onCancel={onCancel}
      onConfirm={submit}
      note={
        isPublic ? 'Visible to all partners' : 'Set visibility permissions via partner detail page'
      }
    >
      <form
        className="assetsForm"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          id="folder-name"
          label="Folder name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Autumn campaign 2026"
          autoComplete="off"
        />

        <div className="assetsFormToggle">
          <span className="fieldLabel">Public</span>
          <Toggle label="Public" labelHidden checked={isPublic} onChange={setIsPublic} />
        </div>
      </form>

      {create.error ? (
        <p className="assetsError" role="alert">
          {create.error instanceof Error ? create.error.message : 'Could not create that folder.'}
        </p>
      ) : null}
    </FormPanel>
  );
}
